import * as React from "react"
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  Button,
  Image,
  ModalProps,
  Icon,
  Input,
  Stack,
  HStack,
} from "@chakra-ui/react"
import { matchSorter } from "match-sorter"
import { FiCheck } from "react-icons/fi"
import { OrgEntity } from "../lib/types/org"
import { useOrgs } from "./providers/OrgProvider"

interface BikeOrgSwitcherProps extends Omit<ModalProps, "children"> {
  org: OrgEntity
  onSelect: (org: OrgEntity) => void
}

export const BikeOrgSwitcher = (props: BikeOrgSwitcherProps) => {
  const { currentOrgs } = useOrgs()

  const [search, setSearch] = React.useState("")
  const [selectedOrg, setSelectedOrg] = React.useState<OrgEntity | undefined>(props.org)

  const filteredOrgs = matchSorter(currentOrgs, search, { keys: ["name"] })

  const handleClose = () => {
    setSearch("")
    props.onClose()
  }

  const handleSave = () => {
    if (selectedOrg) {
      props.onSelect(selectedOrg)
      setSelectedOrg(undefined)
      handleClose()
    }
  }

  return (
    <Modal {...props} isCentered scrollBehavior="inside">
      <ModalOverlay zIndex={100}>
        <ModalContent>
          <ModalHeader>Change Asset Organization</ModalHeader>
          <ModalCloseButton />
          <ModalBody h={550} pb="100px">
            <Input
              mb={4}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
            />
            <Stack>
              {filteredOrgs.map((org: OrgEntity) => (
                <OrgItem
                  key={org.uuid}
                  org={org}
                  isSelected={selectedOrg?.uuid === org.uuid}
                  onToggle={() => {
                    setSelectedOrg(org)
                  }}
                />
              ))}
            </Stack>

            <Flex
              align="center"
              justify="space-between"
              pos="absolute"
              borderRadius="md"
              bottom={0}
              right={0}
              left={0}
              p={4}
              bg="white"
            >
              <HStack>
                <Button variant="ghost" onClick={handleClose}>
                  Cancel
                </Button>
                {selectedOrg && selectedOrg.uuid !== props.org.uuid && (
                  <Button colorScheme="green" onClick={handleSave}>
                    Update
                  </Button>
                )}
              </HStack>
            </Flex>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}

interface OrgItemProps {
  org: OrgEntity
  isSelected: boolean
  onToggle: () => void
}

const OrgItem = ({ org, isSelected, onToggle }: OrgItemProps) => {
  return (
    <Button variant="ghost" tabIndex={0} w="100%" pr={2} pl={0} py={0} onClick={onToggle}>
      <Flex w="100%" justify="space-between" align="center">
        <Flex justify="flex-start" align="center">
          <Image src={org.icon} mr={2} boxSize="34px" objectFit="contain" />
          {org.name}
        </Flex>
        {isSelected && <Icon as={FiCheck} color="green.500" boxSize="30px" />}
      </Flex>
    </Button>
  )
}
